import {
  isModuleEnabled,
  moduleOption,
  type OrgModulesState,
} from "@/modules/registry";
import { useEffect, useMemo, useState } from "react";
import { useGetIdentity } from "ra-core";
import { useSearchParams } from "react-router";
import {
  PageActions,
  PageTitle,
} from "@/components/atomic-crm/layout/PageActions";
import { canAccess } from "@/components/atomic-crm/providers/commons/canAccess";
import type { AccessIdentity } from "@/components/atomic-crm/providers/commons/canAccess";
import { AccountsGroupedList } from "@/modules/accounts/AccountsGroupedList";
import {
  canAccessAccountsBoard,
  canAccessAccountsList,
  resolveDefaultAccountsView,
} from "@/modules/accounts/accountsHubAccess";
import {
  buildAccountsHubViewChangeParams,
  parseAccountsHubView,
  persistAccountsListTab,
  persistAccountsView,
  readPersistedAccountsListTab,
  readPersistedAccountsView,
  syncAccountsHubSearchParams,
  type AccountsHubView,
} from "@/modules/accounts/accountsHubSearchParams";
import type {
  AccountsHubChrome,
  AccountsListTab,
} from "@/modules/accounts/AccountsModuleToolbar";
import {
  AccountsRecordPanel,
  useAccountsPanelSelection,
} from "@/modules/accounts/AccountsRecordPanel";
import { NewClientDialog } from "@/modules/clients/NewClientDialog";
import { NewContactDialog } from "@/modules/clients/NewContactDialog";
import { NewLeadDialog } from "@/modules/leads/NewLeadDialog";
import { LeadsBoardPanel } from "@/modules/leads/LeadsBoardPanel";
import { useIsMobile } from "@/hooks/use-mobile";

/** Accounts hub — List (company-first) | Board (leads Kanban), same docked record panel. */
export const AccountsHubPage = () => {
  const { data: identity } = useGetIdentity();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const panel = useAccountsPanelSelection();

  const orgModules = (identity as { org_modules?: OrgModulesState | null })
    ?.org_modules;
  // Company settings (Apps): people-only accounts; leads board needs Pipeline.
  const peopleOnly = moduleOption(orgModules, "accounts", "peopleOnly");
  const canList = useMemo(() => canAccessAccountsList(identity), [identity]);
  const canBoard =
    useMemo(() => canAccessAccountsBoard(identity), [identity]) &&
    isModuleEnabled(orgModules, "pipeline");

  const canCreateCompany = canAccess(identity as AccessIdentity, {
    resource: "companies",
    action: "create",
  });
  const canCreateContact = canAccess(identity as AccessIdentity, {
    resource: "contacts",
    action: "create",
  });

  const requestedView = useMemo(() => {
    const fromUrl = parseAccountsHubView(searchParams.get("view"));
    if (fromUrl) return fromUrl;
    return readPersistedAccountsView();
  }, [searchParams]);

  const activeView = useMemo(() => {
    const view = resolveDefaultAccountsView(identity, requestedView);
    // The leads board belongs to Pipeline; without it, stay on the list.
    return view === "board" && !canBoard ? "list" : view;
  }, [identity, requestedView, canBoard]);

  useEffect(() => {
    if (!activeView) return;
    persistAccountsView(activeView);
    const next = syncAccountsHubSearchParams(activeView, searchParams);
    if (!next || next.toString() === searchParams.toString()) return;
    setSearchParams(next, { replace: true });
  }, [activeView, searchParams, setSearchParams]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "people" || tab === "companies") persistAccountsListTab(tab);
  }, [searchParams]);

  useEffect(() => {
    const create = searchParams.get("create");
    if (create === "company") setCompanyDialogOpen(true);
    else if (create === "lead") setLeadDialogOpen(true);
    else if (create === "contact") setContactDialogOpen(true);
    else return;
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleViewChange = (view: AccountsHubView) => {
    if (view === "list" && !canList) return;
    if (view === "board" && !canBoard) return;
    persistAccountsView(view);
    const next = buildAccountsHubViewChangeParams(view, searchParams);
    next.delete("contact");
    next.delete("lead");
    next.delete("stage");
    next.delete("company");
    setSearchParams(next, { replace: true });
  };

  if (!identity || !activeView) return null;

  const tabParam = searchParams.get("tab");
  const listTab: AccountsListTab = peopleOnly
    ? "people"
    : tabParam === "people" || tabParam === "companies"
      ? tabParam
      : readPersistedAccountsListTab();
  const handleListTabChange = (tab: AccountsListTab) => {
    persistAccountsListTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === "people") next.set("tab", "people");
    else next.delete("tab");
    next.delete("contact");
    next.delete("lead");
    next.delete("stage");
    next.delete("company");
    setSearchParams(next, { replace: true });
  };

  const chrome: AccountsHubChrome = {
    activeView,
    onViewChange: handleViewChange,
    listTab,
    onListTabChange: peopleOnly ? undefined : handleListTabChange,
    showToggle: canList && canBoard,
    canCreateCompany: canCreateCompany && !peopleOnly,
    canCreateContact,
    onNewCompany: () => setCompanyDialogOpen(true),
    // Leads live in the Pipeline; without it "New contact" is the action.
    onNewLead: isModuleEnabled(orgModules, "pipeline")
      ? () => setLeadDialogOpen(true)
      : undefined,
    onNewContact: () => setContactDialogOpen(true),
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-4 overflow-hidden">
      {isMobile ? null : (
        <PageActions>
          <PageTitle label="Accounts" />
        </PageActions>
      )}

      {activeView === "board" || canList ? (
        <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            {activeView === "board" ? (
              <LeadsBoardPanel embedded accountsChrome={chrome} />
            ) : (
              <AccountsGroupedList accountsChrome={chrome} />
            )}
          </div>
          {panel.open && !isMobile ? (
            <aside className="animate-in fade-in-0 slide-in-from-right-4 flex min-h-0 w-[32rem] max-w-[45%] shrink-0 flex-col overflow-hidden rounded-xl border bg-card duration-200 motion-reduce:animate-none">
              <AccountsRecordPanel />
            </aside>
          ) : null}
        </div>
      ) : null}

      <NewClientDialog
        open={companyDialogOpen}
        onOpenChange={setCompanyDialogOpen}
      />
      <NewLeadDialog open={leadDialogOpen} onOpenChange={setLeadDialogOpen} />
      <NewContactDialog
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
      />

      {isMobile ? <AccountsRecordPanel /> : null}
    </div>
  );
};
